/**
 * `/priorities` — which machine to target first.
 *
 * Reviewer Paul Telford (25 Sep 2026): use "the Driver tree to prioritise
 * which machine to target first". The page leads with the A$ the fleet is
 * exposed to, breaks it down root → mine → machine → driver as an icicle whose
 * block heights are proportional to A$, and ends in a ranked list naming the
 * person who owns each machine's largest driver.
 *
 * ## Data
 *
 * The reliability engine context from {@link useReliability} (five lazy
 * domains plus the operator attribution, built once per page load), passed to
 * `driverTree` and `priorities` through {@link prioritiesView}.
 *
 * UI layer (layer 4). Every projection is in the sibling transform.
 *
 * @see src/versions/v1/modules/fleet/PrioritiesScene.transform.ts
 * @see docs/logic/fleet/priorities.md
 */

import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Calculator, ChevronLeft, UserCheck } from 'lucide-react';
import {
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { useDrawer } from '@/app/providers';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import type { MineCode } from '../../schema/machine';
import type { DriverKind, DriverNode, RootCause } from '../../schema/operations';
import { BY_ID } from '../../data/core';
import { MINE_COL } from '../../shared/oem-engine';
import { MeasureTile, SceneSection, SourceLink, TonePill } from '../../shared/oem-tiles';
import { useReliability } from '../../shared/reliability/useReliability';
import { useToast } from '../../shared/toast/ToastContext';
import { MachineThumb } from '../../shared/viz/Turntable';
import {
  CAUSE_PILL,
  DRIVER_COLOR,
  driverTotals,
  exposureSource,
  icicleLayout,
  pct,
  prioritiesHeadline,
  prioritiesView,
  targetRows,
  type DriverTotal,
  type IcicleCell,
  type TargetRow,
} from './PrioritiesScene.transform';

/** Chart height in px. Labels appear on blocks tall enough to hold them. */
const CHART_H = 560;
/** Rows shown before "Show all". */
const FIRST_ROWS = 10;

const INK = {
  primary: { color: 'var(--ink-primary)' },
  secondary: { color: 'var(--ink-secondary)' },
  tertiary: { color: 'var(--ink-tertiary)' },
} as const satisfies Record<string, CSSProperties>;

export function PrioritiesScene() {
  const { ctx, loading, error } = useReliability();
  const { open: openDrawer } = useDrawer();
  const { show: toast } = useToast();

  const view = useMemo(() => (ctx ? prioritiesView(ctx) : null), [ctx]);
  const headline = useMemo(() => (view ? prioritiesHeadline(view) : null), [view]);
  const drivers = useMemo(() => (view ? driverTotals(view.tree) : []), [view]);
  const rows = useMemo(() => (view ? targetRows(view) : []), [view]);

  const showCalc = useCallback(() => {
    if (!view || !ctx) return;
    const payload = exposureSource(view, ctx);
    openDrawer('oem-source', {
      title: payload.title,
      kicker: 'Source',
      wide: true,
      data: payload as unknown as Record<string, unknown>,
    });
  }, [ctx, openDrawer, view]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PrioritiesScene">
        <SurfacePageBody className="@container max-w-[1240px] space-y-9 pt-6" as="div">
          <SurfacePageHeader
            title="Priorities"
            description="Which machine to target first — the fleet's A$ exposure, broken down by site, machine and driver."
            actions={
              <div className="flex items-center gap-3">
                {headline ? (
                  <TonePill tone="flat">
                    {headline.alerts} open alerts · {headline.flagged} risk-flagged
                  </TonePill>
                ) : null}
                <HelpAnchor doc="fleet/priorities" />
              </div>
            }
          />

          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{ borderColor: 'var(--hairline)', background: 'var(--crit-soft)', color: 'var(--crit)' }}
            >
              The reliability data could not be loaded ({error.message}). No exposure is shown
              rather than a partial one.
            </div>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* Headline                                                   */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="PrioritiesHeadline">
            <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-4">
              {headline ? (
                <>
                  <MeasureTile
                    eyebrow="A$ exposure · open now"
                    value={headline.totalLabel}
                    unit={`across ${headline.machines} machines`}
                    detail={`${headline.totalExact} — the cost of waiting on ${headline.alerts} open alerts plus the risk-weighted cost of ${headline.flagged} likely breakdowns`}
                    tone="bad"
                    onSelect={showCalc}
                    selectHint="Opens the per-machine rows and the calculation."
                  />
                  <MeasureTile
                    eyebrow="Where it concentrates"
                    value={`${headline.paretoCount} of ${headline.machines}`}
                    unit="machines carry 80%"
                    detail={
                      headline.topMachine
                        ? `${headline.topMachine.machine} alone is ${pct(headline.topMachine.share)} — ${headline.topMachine.component}`
                        : undefined
                    }
                    tone="warn"
                  />
                  <MeasureTile
                    eyebrow="Top driver"
                    value={headline.topDriver ? pct(headline.topDriver.share) : '—'}
                    unit={headline.topDriver?.label.toLowerCase()}
                    detail={
                      headline.nextDrivers.length
                        ? `Then ${headline.nextDrivers.map((d) => `${d.label.toLowerCase()} ${pct(d.share)}`).join(' · ')}`
                        : undefined
                    }
                    tone="flat"
                  />
                  <MeasureTile
                    eyebrow="Routed to"
                    value={
                      <>
                        {headline.toMaintenance}
                        <span className="mx-1.5 text-[15px] font-normal" style={INK.tertiary}>
                          /
                        </span>
                        {headline.toOperations}
                      </>
                    }
                    unit="Maintenance / Operations"
                    detail={
                      headline.toOperations
                        ? `${headline.operationsMachines.join(', ')} also goes to the operating supervisor — the root cause involves how it is driven`
                        : 'No machine needs operations — every cause is equipment'
                    }
                    tone="good"
                  />
                </>
              ) : (
                <SkeletonCardGrid count={4} />
              )}
            </div>
          </Commentable>

          <AskStrip view="priorities" />

          {/* ---------------------------------------------------------- */}
          {/* Driver tree                                                */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="PrioritiesDriverTree">
            <SceneSection
              title="Driver tree"
              subtitle="block height is A$"
              actions={
                view ? (
                  <SourceLink onClick={showCalc}>
                    <Calculator className="h-3.5 w-3.5" aria-hidden="true" />
                    How it is calculated
                  </SourceLink>
                ) : null
              }
            >
              {view ? (
                <DriverTree tree={view.tree} drivers={drivers} />
              ) : loading ? (
                <SkeletonBlock className="h-[640px] rounded-xl" />
              ) : null}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Target first                                               */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="PrioritiesTargetList">
            <SceneSection title="Target first" subtitle="ranked by A$ exposure, with who owns the fix">
              {view ? (
                <TargetList rows={rows} onAssign={(row) => toast(row.assignMessage)} />
              ) : loading ? (
                <SkeletonBlock className="h-[420px] rounded-xl" />
              ) : null}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* How exposure is computed                                   */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="PrioritiesMethod">
            <section
              className="rounded-xl border p-5"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="text-[13px] font-semibold tracking-tight" style={INK.primary}>
                  How exposure is computed
                </h2>
                {view ? (
                  <SourceLink onClick={showCalc}>Show the rows and the arithmetic</SourceLink>
                ) : null}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-4 text-[12px] leading-[1.6] @3xl:grid-cols-3" style={INK.secondary}>
                <MethodStep n={1} title="Open alert → cost of inaction">
                  Unplanned minus planned: downtime hours × the site's lost-production rate, plus the
                  parts the failure takes with it and 1.6× labour — less the planned job. The same
                  figure the Alerts page prices with.
                </MethodStep>
                <MethodStep n={2} title="No alert → risk-weighted breakdown">
                  Machines whose predict_risk score sits above the 0.35 watch line carry risk × the
                  cost of a typical breakdown on their class, from work-order history.
                </MethodStep>
                <MethodStep n={3} title="Split by driver">
                  Each machine's A$ is shared across its drivers by weight — risk factor points, the
                  root-cause operator signal, and parts lead time when the part is not on the shelf.
                </MethodStep>
              </div>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

// ===========================================================================
// Driver tree — an icicle, left to right
// ===========================================================================

const COLUMN_HEAD = ['Total', 'Mine', 'Machine', 'Driver'] as const;

/** Background for a block. Mines and machines take the site hue; drivers their own. */
function cellFill(cell: IcicleCell): { background: string; rail: string } {
  if (cell.kind === 'root') return { background: 'var(--surface-3)', rail: 'var(--ink-tertiary)' };
  if (cell.kind === 'driver' && cell.driverKind) {
    const c = DRIVER_COLOR[cell.driverKind];
    return { background: `color-mix(in srgb, ${c} 26%, var(--surface-2))`, rail: c };
  }
  const c = cell.mine ? MINE_COL[cell.mine] : 'var(--ink-tertiary)';
  return {
    background: `color-mix(in srgb, ${c} ${cell.kind === 'mine' ? 20 : 10}%, var(--surface-2))`,
    rail: c,
  };
}

function DriverTree({ tree, drivers }: { tree: DriverNode; drivers: readonly DriverTotal[] }) {
  const navigate = useNavigate();
  const [focus, setFocus] = useState<MineCode | null>(null);
  const [hover, setHover] = useState<IcicleCell | null>(null);
  const [highlight, setHighlight] = useState<DriverKind | null>(null);

  const cells = useMemo(() => icicleLayout(tree, focus), [tree, focus]);
  const columns = useMemo(
    () => [0, 1, 2, 3].map((c) => cells.filter((cell) => cell.column === c)),
    [cells],
  );

  const activate = (cell: IcicleCell) => {
    if (cell.kind === 'root') setFocus(null);
    else if (cell.kind === 'mine') setFocus(focus === cell.mine ? null : (cell.mine ?? null));
    else if (cell.machine) navigate(`/machines/${cell.machine}?tab=reliability`);
  };

  const readout = hover ?? null;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      {/* Legend — also a filter: pick a driver to see where it sits. */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Highlight a driver">
        {drivers.map((d) => {
          const on = highlight === d.kind;
          return (
            <button
              key={d.kind}
              type="button"
              aria-pressed={on}
              onClick={() => setHighlight(on ? null : d.kind)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
                on ? 'font-medium' : '',
              )}
              style={{
                borderColor: on ? DRIVER_COLOR[d.kind] : 'var(--hairline)',
                background: on ? `color-mix(in srgb, ${DRIVER_COLOR[d.kind]} 14%, var(--surface-2))` : 'transparent',
                color: 'var(--ink-secondary)',
              }}
            >
              <span aria-hidden="true" className="h-2 w-2 rounded-sm" style={{ background: DRIVER_COLOR[d.kind] }} />
              {d.label}
              <span className="mono" style={INK.tertiary}>
                {pct(d.share)}
              </span>
            </button>
          );
        })}
        {focus ? (
          <button
            type="button"
            onClick={() => setFocus(null)}
            className="ml-auto inline-flex items-center gap-1 rounded text-[11.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
            style={{ color: 'var(--accent)' }}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            All mines
          </button>
        ) : null}
      </div>

      {/* Readout — the tooltip, pinned where it never covers the chart. */}
      <p
        aria-live="polite"
        className="mb-2 flex min-h-[20px] flex-wrap items-baseline gap-x-2 text-[12px]"
        style={INK.tertiary}
      >
        {readout ? (
          <>
            <span className="min-w-0 truncate" style={INK.secondary}>
              {readout.path}
            </span>
            <span className="mono font-semibold" style={INK.primary}>
              {readout.valueLabel}
            </span>
            <span>· {pct(readout.shareOfTotal)} of the fleet total</span>
          </>
        ) : (
          <span>
            Hover a block to read it · click a mine to focus on it · click a machine or driver to open the machine
          </span>
        )}
      </p>

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.3fr)] gap-1 @xl:grid-cols-[76px_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.3fr)]">
        {COLUMN_HEAD.map((h, i) => (
          <span
            key={h}
            className={cn(
              'pb-1 text-[10px] font-semibold uppercase tracking-[0.1em]',
              i === 0 ? 'hidden @xl:block' : '',
            )}
            style={INK.tertiary}
          >
            {h}
          </span>
        ))}
        {columns.map((col, i) => (
          <div
            key={i}
            className={cn('relative', i === 0 ? 'hidden @xl:block' : '')}
            style={{ height: CHART_H }}
            onMouseLeave={() => setHover(null)}
          >
            {col.map((cell) => (
              <IcicleBlock
                key={cell.id}
                cell={cell}
                focused={focus !== null}
                dimmed={
                  highlight !== null &&
                  (cell.kind === 'driver' ? cell.driverKind !== highlight : cell.kind !== 'root')
                }
                onHover={setHover}
                onActivate={activate}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function IcicleBlock({
  cell,
  focused,
  dimmed,
  onHover,
  onActivate,
}: {
  cell: IcicleCell;
  focused: boolean;
  dimmed: boolean;
  onHover: (cell: IcicleCell | null) => void;
  onActivate: (cell: IcicleCell) => void;
}) {
  const px = cell.h * CHART_H;
  const { background, rail } = cellFill(cell);
  const showLabel = px >= 12;
  const showValue = px >= 34;
  const inert = cell.kind === 'root' && !focused;
  const title = `${cell.path} — ${cell.valueLabel} (${pct(cell.shareOfTotal)} of the fleet total)`;
  const action =
    cell.kind === 'mine'
      ? focused
        ? 'Show all mines'
        : 'Focus on this mine'
      : cell.kind === 'root'
        ? 'Show all mines'
        : 'Open the machine';

  return (
    <button
      type="button"
      disabled={inert}
      title={title}
      aria-label={`${title}. ${action}.`}
      onMouseEnter={() => onHover(cell)}
      onFocus={() => onHover(cell)}
      onBlur={() => onHover(null)}
      onClick={() => onActivate(cell)}
      className="group absolute inset-x-0 overflow-hidden text-left transition-[opacity,filter] duration-150 hover:brightness-[0.97] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_60%,transparent)] disabled:cursor-default"
      style={{
        top: `calc(${cell.y * 100}% + 1px)`,
        height: `max(calc(${cell.h * 100}% - 2px), 1px)`,
        background,
        borderLeft: `3px solid ${rail}`,
        borderRadius: 4,
        opacity: dimmed ? 0.28 : 1,
      }}
    >
      {showLabel ? (
        <span
          className={cn(
            'flex h-full flex-col px-2',
            showValue ? 'justify-start py-[3px]' : 'justify-center',
          )}
        >
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span
              className={cn('truncate font-medium', px < 17 ? 'text-[10.5px] leading-[12px]' : 'text-[11.5px] leading-[15px]')}
              style={INK.primary}
            >
              {cell.label}
            </span>
            {!showValue ? (
              <span className="mono ml-auto shrink-0 text-[10px] leading-[12px]" style={INK.secondary}>
                {cell.valueLabel}
              </span>
            ) : null}
          </span>
          {showValue ? (
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="mono text-[11px] font-semibold leading-[15px]" style={INK.secondary}>
                {cell.valueLabel}
              </span>
              {cell.sublabel && px >= 34 ? (
                <span className="truncate text-[10.5px] leading-[15px]" style={INK.tertiary}>
                  {cell.sublabel}
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

// ===========================================================================
// Target first
// ===========================================================================

function CausePill({ cause }: { cause: RootCause }) {
  const c = CAUSE_PILL[cause];
  return (
    <span
      title={c.hint}
      className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10.5px] font-medium leading-[1.6]"
      style={{ background: `color-mix(in srgb, ${c.color} 14%, transparent)`, color: `color-mix(in srgb, ${c.color} 80%, var(--ink-primary))` }}
    >
      {c.label}
    </span>
  );
}

function DriverChip({ kind, label, share }: { kind: DriverKind; label: string; share: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px]" style={INK.secondary}>
      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-sm" style={{ background: DRIVER_COLOR[kind] }} />
      <span className="truncate">{label}</span>
      <span className="mono shrink-0 text-[11px]" style={INK.tertiary}>
        {share}
      </span>
    </span>
  );
}

const ROW_GRID =
  '@3xl:grid @3xl:grid-cols-[28px_minmax(0,1.7fr)_minmax(0,1.15fr)_minmax(0,1.1fr)_minmax(0,1.25fr)_auto] @3xl:items-center @3xl:gap-4';

function TargetList({ rows, onAssign }: { rows: readonly TargetRow[]; onAssign: (row: TargetRow) => void }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, FIRST_ROWS);

  return (
    <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}>
      <div
        className={cn('hidden px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em]', ROW_GRID)}
        style={{ ...INK.tertiary, borderBottom: '1px solid var(--hairline)' }}
      >
        <span>#</span>
        <span>Machine</span>
        <span>A$ exposure</span>
        <span>Top driver · cause</span>
        <span>Owner</span>
        <span className="sr-only">Action</span>
      </div>
      <ol>
        {shown.map((row, i) => (
          <li
            key={row.machine}
            className={cn('relative px-4 py-3', ROW_GRID)}
            style={i ? { borderTop: '1px solid var(--hairline)' } : undefined}
          >
            {/* Rank + machine */}
            <span
              className="mono absolute left-4 top-3 text-[13px] font-semibold @3xl:static"
              style={row.rank <= 3 ? { color: 'var(--crit)' } : INK.tertiary}
            >
              {row.rank}
            </span>
            <div className="flex min-w-0 items-center gap-3 pl-7 @3xl:pl-0">
              <MachineThumbCell machine={row.machine} />
              <div className="min-w-0">
                <Link
                  to={row.machineHref}
                  className="block truncate text-[13.5px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                  style={INK.primary}
                >
                  {row.machine}
                  <span className="font-normal" style={INK.secondary}>
                    {' · '}
                    {row.component}
                  </span>
                </Link>
                <span className="block truncate text-[11.5px]" style={INK.tertiary}>
                  {row.mineName}
                  {' · '}
                  {row.alertId && row.alertHref ? (
                    <Link to={row.alertHref} className="underline decoration-dotted underline-offset-[3px] hover:decoration-solid" style={{ color: 'var(--accent)' }}>
                      {row.alertId}
                    </Link>
                  ) : (
                    row.basisLabel
                  )}
                </span>
              </div>
            </div>

            {/* Exposure */}
            <div className="mt-2 min-w-0 pl-7 @3xl:mt-0 @3xl:pl-0">
              <div className="flex items-baseline gap-2">
                <span className="mono text-[13.5px] font-semibold" style={INK.primary}>
                  {row.valueLabel}
                </span>
                <span className="text-[11px]" style={INK.tertiary}>
                  {row.shareLabel}
                </span>
              </div>
              <div className="mt-1 h-1 w-full max-w-[160px] overflow-hidden rounded-full" style={{ background: 'var(--surface-3)' }}>
                <div className="h-full rounded-full" style={{ width: `${Math.max(row.bar * 100, 2)}%`, background: 'var(--crit)' }} />
              </div>
            </div>

            {/* Driver + cause */}
            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pl-7 @3xl:mt-0 @3xl:flex-col @3xl:items-start @3xl:pl-0" title={row.topDriverWhy}>
              <DriverChip kind={row.topDriver} label={row.topDriverLabel} share={row.topDriverShare} />
              <span className="inline-flex items-center gap-1.5">
                <CausePill cause={row.cause} />
                <span className="text-[10.5px]" style={INK.tertiary}>
                  {row.confidence} conf.
                </span>
              </span>
            </div>

            {/* Owner */}
            <div className="mt-2 min-w-0 pl-7 @3xl:mt-0 @3xl:pl-0">
              <span className="block truncate text-[12.5px] font-medium" style={INK.primary}>
                {row.ownerName}
              </span>
              <span className="block truncate text-[11px]" style={INK.tertiary}>
                {row.ownerRole} · {row.ownerChannel}
              </span>
            </div>

            {/* Action */}
            <div className="mt-2 pl-7 @3xl:mt-0 @3xl:pl-0">
              <button
                type="button"
                onClick={() => onAssign(row)}
                className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                style={{ borderColor: 'var(--hairline)', color: 'var(--accent)' }}
              >
                <UserCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Assign
              </button>
            </div>
          </li>
        ))}
      </ol>
      {rows.length > FIRST_ROWS ? (
        <div className="px-4 py-2.5" style={{ borderTop: '1px solid var(--hairline)' }}>
          <button
            type="button"
            onClick={() => setAll((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded text-[12px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
            style={{ color: 'var(--accent)' }}
          >
            {all ? `Show the top ${FIRST_ROWS}` : `Show all ${rows.length} machines`}
            <ArrowRight className={cn('h-3.5 w-3.5 transition-transform', all ? '-rotate-90' : 'rotate-90')} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MachineThumbCell({ machine }: { machine: string }) {
  const m = BY_ID[machine];
  return (
    <span
      aria-hidden="true"
      className="hidden h-[30px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-md @md:flex"
      style={{ background: 'var(--surface-1)' }}
    >
      {m ? <MachineThumb machine={m} className="h-full w-full" /> : null}
    </span>
  );
}

function MethodStep({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span
        aria-hidden="true"
        className="mono flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10.5px] font-semibold"
        style={{ background: 'var(--surface-3)', color: 'var(--ink-secondary)' }}
      >
        {n}
      </span>
      <div className="min-w-0">
        <p className="text-[12.5px] font-medium" style={INK.primary}>
          {title}
        </p>
        <p className="mt-0.5">{children}</p>
      </div>
    </div>
  );
}
